import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import client from '../api/client';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function PlaceTracking() {
  const [places, setPlaces] = useState<any[]>([]);
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const [keywords, setKeywords] = useState<Record<number, any[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await client.get('/place-rank/places');
      const ps = (res.data.places || []).filter((p: any) => p.auto_track === 1 || p.auto_track === true);
      setPlaces(ps);
      const initCollapsed: Record<number, boolean> = {};
      ps.forEach((p: any) => { initCollapsed[p.id] = true; });
      setCollapsed(initCollapsed);
      // 각 플레이스의 히스토리 로드
      const kwMap: Record<number, any[]> = {};
      await Promise.all(ps.map(async (p: any) => {
        try {
          const r = await client.get(`/place-rank/places/${p.id}/history`);
          kwMap[p.id] = r.data.keywords || [];
        } catch { kwMap[p.id] = []; }
      }));
      setKeywords(kwMap);
    } catch {} finally { setLoading(false); }
  };

  const toggleCollapse = (id: number) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  const handleDeleteKeyword = async (placeId: number, keyword: string) => {
    if (!window.confirm(`"${keyword}" 키워드 데이터를 삭제할까요?`)) return;
    try {
      await client.delete(`/place-rank/places/${placeId}/keywords/${encodeURIComponent(keyword)}`);
      setKeywords(prev => ({ ...prev, [placeId]: prev[placeId].filter(k => k.keyword !== keyword) }));
    } catch { alert('삭제 실패'); }
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 },
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
      return (
        <div style={{ background: '#1E2535', border: '0.5px solid #2A3045', borderRadius: 6, padding: '8px 12px' }}>
          <div style={{ color: '#8892A4', fontSize: 11 }}>{label}</div>
          <div style={{ color: '#00D4AA', fontSize: 14, fontWeight: 600 }}>{payload[0].value ? `${payload[0].value}위` : '미노출'}</div>
        </div>
      );
    }
    return null;
  };

  const getStats = (history: any[]) => {
    const ranks = history.filter(h => h.rank).map(h => h.rank);
    if (!ranks.length) return null;
    return {
      best: Math.min(...ranks),
      worst: Math.max(...ranks),
      avg: Math.round(ranks.reduce((a, b) => a + b, 0) / ranks.length)
    };
  };

  const getAlerts = (kws: any[]) => kws.filter(kw => {
    if (!kw.current_rank || kw.history.length < 2) return false;
    const prev = kw.history[kw.history.length - 2]?.rank;
    return prev && Math.abs(kw.current_rank - prev) >= 5;
  });

  const getSummary = (kws: any[]) => {
    const ranks = kws.filter(k => k.current_rank).map(k => k.current_rank);
    return {
      bestRank: ranks.length ? Math.min(...ranks) : null,
      alertCount: getAlerts(kws).length,
      total: kws.length,
    };
  };

  return (
    <Layout>
      <div>
        <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>플레이스 순위 변동 추적</h2>
        <p style={{ color: '#8892A4', fontSize: 12, margin: '0 0 20px' }}>키워드별 순위 변동 이력을 확인하세요</p>

        {loading && <div style={{ color: '#8892A4', fontSize: 14 }}>불러오는 중...</div>}

        {!loading && places.length === 0 && (
          <div style={{ ...s.card, textAlign: 'center', padding: 40 }}>
            <div style={{ color: '#8892A4', fontSize: 14 }}>등록된 플레이스가 없어요</div>
          </div>
        )}

        {places.map(p => {
          const isCollapsed = collapsed[p.id];
          const kws = keywords[p.id] || [];
          // 마지막 검색 키워드 필터링
          let filteredKws = kws;
          if (p.last_keywords) {
            try {
              const lastKws: string[] = JSON.parse(p.last_keywords);
              filteredKws = kws.filter(k => lastKws.includes(k.keyword));
            } catch {}
          }
          const alerts = getAlerts(filteredKws);
          const summary = getSummary(filteredKws);

          return (
            <div key={p.id} style={s.card}>
              {/* 헤더 */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: isCollapsed ? 0 : 16, paddingBottom: isCollapsed ? 0 : 16, borderBottom: isCollapsed ? 'none' : '0.5px solid #2A3045' }}>
                {p.thumbnail ? (
                  <img src={p.thumbnail} alt={p.name} style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: 6, background: '#0F1117', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 24 }}>📍</span>
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{p.name || '플레이스'}</div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ color: '#4A5568', fontSize: 12 }}>키워드 {summary.total}개</span>
                    {summary.bestRank && <span style={{ color: '#00D4AA', fontSize: 12 }}>최고 {summary.bestRank}위</span>}
                    {summary.alertCount > 0 && (
                      <span style={{ background: '#2A1A1A', color: '#F56565', fontSize: 11, padding: '2px 8px', borderRadius: 4 }}>급변동 {summary.alertCount}개</span>
                    )}
                  </div>
                </div>
                <button onClick={() => toggleCollapse(p.id)}
                  style={{ background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '4px 12px', color: '#8892A4', fontSize: 12, cursor: 'pointer' }}>
                  {isCollapsed ? '펼치기 ▼' : '접기 ▲'}
                </button>
              </div>

              {!isCollapsed && (
                <>
                  {/* 급변동 알림 */}
                  {alerts.length > 0 && (
                    <div style={{ background: '#1A1A2A', border: '0.5px solid #2A2A4A', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                      <div style={{ color: '#8892A4', fontSize: 11, marginBottom: 8 }}>⚡ 급변동 알림 (5위 이상 변동)</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {alerts.map((kw: any) => {
                          const prev = kw.history[kw.history.length - 2]?.rank;
                          const diff = prev - kw.current_rank;
                          return (
                            <div key={kw.keyword} style={{ background: diff > 0 ? '#0F2A22' : '#2A1A1A', borderRadius: 6, padding: '4px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span style={{ color: '#E8ECF4', fontSize: 12 }}>{kw.keyword}</span>
                              <span style={{ color: diff > 0 ? '#00D4AA' : '#F56565', fontSize: 12, fontWeight: 700 }}>
                                {diff > 0 ? `▲ ${diff}` : `▼ ${Math.abs(diff)}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* 키워드 목록 */}
                  {filteredKws.length === 0 ? (
                    <div style={{ color: '#4A5568', fontSize: 13 }}>순위 조회 후 데이터가 쌓이면 표시됩니다</div>
                  ) : (
                    filteredKws.map((kw: any) => {
                      const stats = getStats(kw.history);
                      const graphData = kw.history.map((h: any) => ({
                        date: h.checked_at?.split(' ')[0] || h.checked_at,
                        rank: h.rank || null
                      }));
                      const prev = kw.history.length >= 2 ? kw.history[kw.history.length - 2]?.rank : null;
                      const change = kw.current_rank && prev ? prev - kw.current_rank : null;

                      return (
                        <div key={kw.keyword} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '0.5px solid #2A3045' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{kw.keyword}</span>
                              <button onClick={() => handleDeleteKeyword(p.id, kw.keyword)}
                                style={{ background: 'none', border: '0.5px solid #4A2A2A', borderRadius: 4, padding: '2px 8px', color: '#F56565', fontSize: 10, cursor: 'pointer' }}>삭제</button>
                              {change !== null && (
                                <span style={{ background: change > 0 ? '#0F2A22' : change < 0 ? '#2A1A1A' : '#1A2035', color: change > 0 ? '#00D4AA' : change < 0 ? '#F56565' : '#8892A4', fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                                  {change > 0 ? `▲ ${change}` : change < 0 ? `▼ ${Math.abs(change)}` : '유지'}
                                </span>
                              )}
                            </div>
                            {kw.current_rank ? (
                              <div style={{ background: '#0D1F35', borderRadius: 6, padding: '4px 12px', textAlign: 'center' }}>
                                <div style={{ fontSize: 10, color: '#4A5568' }}>순위</div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: '#00D4AA' }}>{kw.current_rank}위</div>
                              </div>
                            ) : (
                              <span style={{ color: '#F56565', fontSize: 13 }}>100위 밖</span>
                            )}
                          </div>

                          {stats && (
                            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                              <div style={{ background: '#0F1117', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
                                <span style={{ color: '#4A5568' }}>최고 </span>
                                <span style={{ color: '#00D4AA', fontWeight: 600 }}>{stats.best}위</span>
                              </div>
                              <div style={{ background: '#0F1117', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
                                <span style={{ color: '#4A5568' }}>최저 </span>
                                <span style={{ color: '#F56565', fontWeight: 600 }}>{stats.worst}위</span>
                              </div>
                              <div style={{ background: '#0F1117', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
                                <span style={{ color: '#4A5568' }}>평균 </span>
                                <span style={{ color: '#8892A4', fontWeight: 600 }}>{stats.avg}위</span>
                              </div>
                            </div>
                          )}

                          {graphData.length > 1 && (
                            <div style={{ marginBottom: 12 }}>
                              <ResponsiveContainer width="100%" height={100}>
                                <LineChart data={graphData}>
                                  <XAxis dataKey="date" tick={{ fill: '#4A5568', fontSize: 9 }} />
                                  <YAxis reversed domain={['dataMin - 2', 'dataMax + 2']} tick={{ fill: '#4A5568', fontSize: 9 }} />
                                  <Tooltip content={<CustomTooltip />} />
                                  <Line type="monotone" dataKey="rank" stroke="#00D4AA" strokeWidth={2}
                                    dot={(props: any) => {
                                      const { cx, cy, payload } = props;
                                      if (!payload.rank) return <circle key={props.key} cx={cx} cy={cy} r={4} fill="#F56565" />;
                                      return <circle key={props.key} cx={cx} cy={cy} r={3} fill="#00D4AA" />;
                                    }}
                                    connectNulls={false} />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          )}

                          {kw.history.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                              <div style={{ color: '#4A5568', fontSize: 11, paddingBottom: 4 }}>날짜</div>
                              <div style={{ color: '#4A5568', fontSize: 11, paddingBottom: 4, textAlign: 'right' }}>순위</div>
                              {[...kw.history].reverse().slice(0, 7).map((h: any, j: number) => (
                                <>
                                  <div key={`d${j}`} style={{ color: '#8892A4', fontSize: 12, padding: '2px 0' }}>{h.checked_at?.split(' ')[0]}</div>
                                  <div key={`r${j}`} style={{ color: h.rank ? '#00D4AA' : '#F56565', fontSize: 12, padding: '2px 0', textAlign: 'right', fontWeight: 600 }}>
                                    {h.rank ? `${h.rank}위` : '노출없음'}
                                  </div>
                                </>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
